import {
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Power,
  PowerOff,
  Star,
  Trash2,
  Wifi,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ModelList } from "./ModelList";

export interface Provider {
  id: string;
  type: "storage" | "email" | "ai";
  providerName: string;
  displayName: string;
  isActive: boolean;
  isDefault: boolean;
  connectionStatus: "connected" | "failed" | "untested";
  lastTestedAt: string | null;
  settings: Record<string, string>;
  credentials: Record<string, boolean>;
  modelCount?: number;
  createdAt: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  s3: "S3",
  smtp: "SMTP",
  resend: "Resend",
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
  ollama: "Ollama",
};

function StatusDot({ status }: { status: Provider["connectionStatus"] }) {
  const colors = {
    connected: "bg-emerald-500",
    failed: "bg-red-500",
    untested: "bg-gray-400",
  };
  const labels = {
    connected: "Connected",
    failed: "Failed",
    untested: "Not tested",
  };
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={`inline-block h-2 w-2 rounded-full ${colors[status]}`} />
      {labels[status]}
    </span>
  );
}

function providerMeta(provider: Provider): string {
  const { type, settings } = provider;
  if (type === "storage") {
    const parts: string[] = [];
    if (settings.bucket) parts.push(settings.bucket);
    if (settings.region) parts.push(settings.region);
    return parts.join(" / ") || "No bucket configured";
  }
  if (type === "email") {
    return settings.fromAddress || "No sender configured";
  }
  if (type === "ai") {
    const count = provider.modelCount ?? 0;
    return `${count} model${count === 1 ? "" : "s"}`;
  }
  return "";
}

interface ProviderCardProps {
  provider: Provider;
  onEdit: () => void;
  onDeleted: () => void;
  onRefresh: () => void;
}

export function ProviderCard({ provider, onEdit, onDeleted, onRefresh }: ProviderCardProps) {
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  async function handleTest() {
    setTesting(true);
    try {
      const res = await fetch(`/api/providers/${provider.id}/test`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.status === "connected") {
        toast.success(data.message || "Connection successful");
      } else {
        toast.error(data.message || "Connection failed");
      }
      onRefresh();
    } catch {
      toast.error("Failed to test connection");
    } finally {
      setTesting(false);
    }
  }

  async function handleToggleActive() {
    setToggling(true);
    const action = provider.isActive ? "deactivate" : "activate";
    try {
      const res = await fetch(`/api/providers/${provider.id}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to ${action}`);
      }
      toast.success(`Provider ${action}d`);
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setToggling(false);
    }
  }

  async function handleSetDefault() {
    setSettingDefault(true);
    try {
      const res = await fetch(`/api/providers/${provider.id}/set-default`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to set default");
      }
      toast.success("Provider set as default");
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to set default");
    } finally {
      setSettingDefault(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${provider.displayName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/providers/${provider.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete");
      }
      toast.success("Provider deleted");
      onDeleted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="truncate text-sm font-semibold">{provider.displayName}</h4>
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {PROVIDER_LABELS[provider.providerName] ?? provider.providerName}
            </Badge>
            {provider.isActive && (
              <Badge
                variant="secondary"
                className="shrink-0 text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              >
                Active
              </Badge>
            )}
            {provider.isDefault && (
              <Badge
                variant="secondary"
                className="shrink-0 text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400"
              >
                Default
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3">
            <StatusDot status={provider.connectionStatus} />
            <span className="text-xs text-muted-foreground">{providerMeta(provider)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-wrap">
        <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
          {testing ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wifi className="mr-1.5 h-3.5 w-3.5" />
          )}
          Test
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>
        {!provider.isDefault && (
          <Button variant="ghost" size="sm" onClick={handleSetDefault} disabled={settingDefault}>
            {settingDefault ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Star className="mr-1.5 h-3.5 w-3.5" />
            )}
            Set Default
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={handleToggleActive} disabled={toggling}>
          {toggling ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : provider.isActive ? (
            <PowerOff className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Power className="mr-1.5 h-3.5 w-3.5" />
          )}
          {provider.isActive ? "Deactivate" : "Activate"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Delete
        </Button>
      </div>

      {/* Expandable AI models section */}
      {provider.type === "ai" && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            {expanded ? "Hide models" : "Show models"}
          </button>
          {expanded && (
            <div className="border-t pt-3">
              <ModelList providerId={provider.id} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
